import { useMemo } from "react";
import { useTranslation } from "translation/useTranslation";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ListPagination from "components/ui/ListPagination";
import { Input, InputAffix } from "components/ui";
import "react-tooltip/dist/react-tooltip.css";

function EmployeeSelectorOption({ employee, checked, onToggle }) {
  const tooltipId = employee.tooltip ? `employee-option-tooltip-${employee.hash_id}` : undefined;

  return (
    <div className="col-xl-4 mb-2" data-testid={`employee-option-${employee.hash_id}`}>
      {employee.tooltip && <ReactTooltip id={tooltipId} content={employee.tooltip} place="right" />}
      <div className="form-check">
        <label className="form-check-label">
          <input
            className="form-check-input"
            type="checkbox"
            checked={checked}
            onChange={e => onToggle(employee.hash_id, e.target.checked)}
            data-testid={`employee-option-checkbox-${employee.hash_id}`}
          />
          <span className="ml-1" data-tooltip-id={tooltipId}>
            {
              employee.label
                ? employee.label
                : <>
                  {`${employee.first_name || ''} ${employee.last_name || ''}`.trim()}
                  {employee.employee_number ? <span className="text-muted ml-1">({employee.employee_number})</span> : null}
                </>
            }
          </span>
        </label>
      </div>
    </div>
  );
}

export default function EmployeeSelector({
  employees,
  selected,
  setSelected,
  onSelectionChange,
  search,
  onSearchChange,
  total,
  loadingPool,
  page,
  perPage,
  totalPages,
  totalRows,
  setPage,
  setPerPage,
}) {
  const { translate, editableTranslate } = useTranslation();

  const isAllChecked = useMemo(() => {
    return employees.length > 0 && employees.every(e => selected.includes(e.hash_id));
  }, [employees, selected]);

  const handleToggleAll = (checked) => {
    const pageIds = employees.map(e => e.hash_id);
    setSelected(prev => {
      if (checked) return Array.from(new Set([...prev, ...pageIds]));
      return prev.filter(id => !pageIds.includes(id));
    });
    if (onSelectionChange) onSelectionChange(employees, checked);
  };

  const handleToggle = (hashId, checked) => {
    setSelected(prev => {
      if (checked) {
        return prev.includes(hashId) ? prev : [...prev, hashId];
      }
      return prev.filter(id => id !== hashId);
    });
    if (onSelectionChange) {
      const employee = employees.find(e => e.hash_id === hashId) || { hash_id: hashId };
      onSelectionChange([employee], checked);
    }
  };

  return (
    <div className="user-groups-selector-menu" data-testid="employee-selector">
      <div className="d-flex justify-content-between align-items-center user-groups-selector-search-div">
        <div className="w-100 mr-2">
          <InputAffix suffix={<i className="bi bi-search ml-2"></i>}>
            <Input
              type="text"
              placeholder={translate('global.search', 'Suche')}
              autoComplete="false"
              maxLength={255}
              value={search}
              data-testid="employee-search"
              onChange={e => onSearchChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }}
            />
          </InputAffix>
        </div>
        <div className="text-muted text-nowrap">
          {selected.length} / {total}
        </div>
      </div>

      <div className="group-category-div mb-1">
        <div className="category-label-div">
          <div className="form-check">
            <label className="form-check-label">
              <input
                className="form-check-input"
                type="checkbox"
                checked={isAllChecked}
                data-testid="employee-select-all"
                onChange={e => handleToggleAll(e.target.checked)}
              />
              <span className="ml-1">{editableTranslate('action.detail.select_all_page', 'Alle auf dieser Seite auswählen')}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="group-category-detail">
        {
          loadingPool && (
            <div className="text-center p-2" data-testid="employee-loading">
              <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Wird geladen...</span></div>
            </div>
          )
        }
        <div className="row">
          {
            employees.length > 0
              ? employees.map(employee => (
                <EmployeeSelectorOption
                  key={employee.hash_id}
                  employee={employee}
                  checked={selected.includes(employee.hash_id)}
                  onToggle={handleToggle}
                />
              ))
              : !loadingPool && <div className="col" data-testid="employee-empty">{editableTranslate('global.no_data', 'Keine Daten')}</div>
          }
        </div>
      </div>

      <ListPagination
        page={page}
        perPage={perPage}
        totalPages={totalPages}
        totalRows={totalRows}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </div>
  );
}
